import React from "react";
import styled from "styled-components";
import { Activity } from "../../../amorg-common";
import { colors } from "../../../designSystem/colors/color";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { OnlyOnPrint, PrintBreakAfter, PrintStyle } from "../../../designSystem/compositeComponents/Print";
import { P, Small } from "../../../designSystem/typography/Body";
import { H1, H2, H3 } from "../../../designSystem/typography/Heading";
import { toDateString, toTimeString } from "../../../utils/date";


type PrintMeetingActivityProps = {
    activity:Activity,
    innerRef: React.Ref<HTMLDivElement>
}

function getActivityRoleText(activityRole:Activity['participants'][number]['activityRole']):string
{
    if(activityRole === 'facilitator') return 'Facilitator / mødeleder';
    if(activityRole === 'minuteTaker') return 'Referent';
    return 'Deltager';
}

export default function PrintMeetingActivity(props: PrintMeetingActivityProps): JSX.Element 
{
    return (
        <>

        <OnlyOnPrint>
            <PrintStyle ref={props.innerRef}>
                
                <PrintBreakAfter>
                    <Flex flexDirection="column" gap={16}>
                        <Flex flexDirection="column" gap={8}>
                            
                            <H1>{props.activity.title}</H1>
                            <P>{props.activity.description}</P>
                            <MeetingTopSection>
                                <Flex flexDirection="row" gap={32} justifyContent='space-between'>
                                    <P>Dato: {toDateString(new Date(props.activity.start))}</P>
                                    <P>Tid: {toTimeString(new Date(props.activity.start))}</P>
                                    <P>Sted: {props.activity.location}</P>
                                </Flex>
                            </MeetingTopSection>
                        </Flex>
                        <Flex flexDirection="column" gap={8}>
                            <H2>Deltagere</H2>
                            {props.activity.participants.map( (participant, i) => (
                                <P key={i}>{participant.name} - {getActivityRoleText(participant.activityRole)}</P>
                            ))}
                        </Flex>
                    </Flex>
                </PrintBreakAfter>
                    
                <Flex flexDirection="column" gap={64}>
                
                    {props.activity.agendaItems.map( (item, i) => (
                        
                        <Flex flexDirection="row" gap={8} key={i}>
                            <AgendaItemHeader>
                                <H2>{i + 1}</H2>    
                            </AgendaItemHeader>
                            <Flex flexDirection="column" gap={8} key={i}>
                                <AgendaItemHeader border>
                                    <H2>{item.title}</H2>
                                </AgendaItemHeader>
                                 {item.description && (
                                     <Flex flexDirection="column" gap={4}>
                                         <H3>Beskrivelse</H3>
                                         <P dangerouslySetInnerHTML={{ __html: item.description }} />
                                     </Flex>
                                 )}
                                 {item.note && (
                                     <Flex flexDirection="column" gap={4}>
                                         <H3>Referat</H3>
                                         <P dangerouslySetInnerHTML={{ __html: item.note }} />
                                     </Flex>
                                 )}
                                
                                {item.actionPoints.length > 0 &&
                                    <Flex flexDirection="column" gap={4}>
                                        <>
                                            <H3>Handlepunkter</H3>
                                            {item.actionPoints.map( (actionPoint, i) => (
                                                <Flex flexDirection="row" gap={8} key={i}>
                                                    <P>{i + 1}:</P>
                                                    <Flex flexDirection="column" gap={4}>
                                                        <P>{actionPoint.description}</P>
                                                        <Flex flexDirection='row' gap={16}>
                                                            {actionPoint.assignedUser &&
                                                                <Small>Ansvarlig: {actionPoint.assignedUser?.fullName}</Small>
                                                            }
                                                            {actionPoint.deadline &&
                                                                <Small>Deadline: {toDateString(new Date(actionPoint.deadline))}</Small>
                                                            }
                                                            {actionPoint.done &&
                                                                <Small>Gennemført!</Small>
                                                            }
                                                        </Flex>
                                                    </Flex>
                                                </Flex>
                                            ))}
                                        </>
                                    </Flex>
                                }   
                            </Flex>
                        </Flex>
                        
                    ))}
                        
                </Flex>
            </PrintStyle>
        </OnlyOnPrint>
        </>
    );

}

const MeetingTopSection = styled.div`
    border-bottom: 1px solid ${'red'};
    border-top: 1px solid ${colors.divider};
    padding-bottom: 16px;
    padding-top: 16px;
`

const AgendaItemHeader = styled.div<{border?:boolean}>`
    border-bottom: ${props => props.border ? `1px solid ${colors.divider}` : '1px solid transparent'};   
    border-top: ${props => props.border ? `1px solid ${colors.divider}` : '1px solid transparent'};   
`